<script setup lang="ts">
const { customSealsOnBottomSeals } = useLicenses();

type V2DynamicSeal = {
  id: string;
  href: string;
  src: string;
  alt: string;
  width: number;
  height: number;
  mobileWidth: number;
  mobileHeight: number;
};

const FIGMA_SIZES: Record<
  string,
  { width: number; height: number; mobileWidth: number; mobileHeight: number }
> = {
  'OD-Azul': { width: 43, height: 42, mobileWidth: 31, mobileHeight: 38 },
  IC360: { width: 110, height: 28, mobileWidth: 80, mobileHeight: 20 },
};

type RawSeal = {
  id?: string;
  src?: string;
  href?: string;
  alt?: string;
};

const dynamicSeals = computed<V2DynamicSeal[]>(() => {
  const rawList = (customSealsOnBottomSeals.value ?? []) as RawSeal[];

  return rawList
    .map((raw): V2DynamicSeal | null => {
      const id = raw?.id;

      if (!id) return null;

      const sizeToken = FIGMA_SIZES[id];

      if (!sizeToken) return null;

      const src = raw.src ? getAssetsUrlForCDN(raw.src) : '';

      return {
        id,
        src,
        href: raw?.href || '',
        alt: raw?.alt || id,
        width: sizeToken.width,
        height: sizeToken.height,
        mobileWidth: sizeToken.mobileWidth,
        mobileHeight: sizeToken.mobileHeight,
      };
    })
    .filter((seal): seal is V2DynamicSeal => seal !== null && !!seal.src);
});

const raReputationSeal = computed(() => {
  const rawList = (customSealsOnBottomSeals.value ?? []) as RawSeal[];

  return rawList.find((s) => s.id === 'reclame-aqui') ?? null;
});
</script>

<template>
  <div class="footer-v2-seals">
    <p class="footer-v2-seals__title">
      <!-- Mobile mantém "Selos:". Desktop usa "Nossos selos:" (Figma). -->
      <span class="lg:hidden">Selos:</span>
      <span class="hidden lg:inline">Nossos selos:</span>
    </p>

    <div class="footer-v2-seals__items">
      <FooterV2AuthorizedSeal
        class="hidden lg:flex"
        embedded
      />

      <FooterItemsSeal
        class="footer-v2-seals__begamble"
        href="https://www.begambleaware.org/"
        :image="getAssetsUrlForCDN('/assets/seals/BeGambleAware.webp')"
        title="BeGambleAware"
      />

      <FooterItemsSeal
        class="footer-v2-seals__gt"
        href="https://www.gamblingtherapy.org/pt-br/"
        :image="getAssetsUrlForCDN('/assets/seals/gt_logo_v2.svg')"
        :title="$t('footer.game_therapy')"
      />

      <NuxtLink
        class="footer-v2-seals__responsible"
        no-prefetch
        to="/page/responsible-gaming"
      >
        <BaseIconsResponsibleGamingLogo
          class="footer-v2-seals__parent-advisor"
        />
      </NuxtLink>

      <template
        v-for="seal in dynamicSeals"
        :key="seal.id"
      >
        <a
          v-if="seal.href"
          :aria-label="seal.alt"
          class="footer-v2-seals__dynamic"
          :href="seal.href"
          rel="noopener noreferrer"
          :style="{
            '--seal-w': `${seal.width}px`,
            '--seal-h': `${seal.height}px`,
            '--seal-w-mobile': `${seal.mobileWidth}px`,
            '--seal-h-mobile': `${seal.mobileHeight}px`,
          }"
          target="_blank"
        >
          <img
            :alt="seal.alt"
            decoding="async"
            :height="seal.height"
            loading="lazy"
            :src="seal.src"
            :width="seal.width"
          />
        </a>

        <img
          v-else
          :alt="seal.alt"
          class="footer-v2-seals__dynamic"
          decoding="async"
          :height="seal.height"
          loading="lazy"
          :src="seal.src"
          :style="{
            '--seal-w': `${seal.width}px`,
            '--seal-h': `${seal.height}px`,
            '--seal-w-mobile': `${seal.mobileWidth}px`,
            '--seal-h-mobile': `${seal.mobileHeight}px`,
          }"
          :width="seal.width"
        />
      </template>

      <a
        v-if="raReputationSeal"
        class="footer-v2-seals__ra"
        :href="raReputationSeal.href || undefined"
        rel="noopener noreferrer"
        target="_blank"
      >
        <img
          alt="Empresa RA1000 pelo Selo Reclame AQUI"
          :src="getAssetsUrlForCDN('/assets/seals/ra1000.png')"
        />
      </a>
    </div>
  </div>
</template>

<style scoped lang="scss">
.footer-v2-seals {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  /* Desktop:
       padding 20px 36px / gap 40px / flex 1 0 0 (ocupa o restante do
       espaço do BottomBar) / align-self stretch / border-radius 9px /
       border branca 10%. Layout row: título "Selos:" à esquerda + items
       à direita (mesmo padrão do Sponsors). */
  @media (min-width: 1024px) {
    flex-direction: row;
    padding: 20px 36px;
    gap: 40px;
    flex: 1 0 0;
    border-radius: 9px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  &__title {
    margin: 0;
    color: rgba(255, 255, 255, 0.5); // #FFFFFF80
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
  }

  &__items {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    width: 100%;
    padding: 0 10px;
    justify-items: center;
    align-items: center;
    gap: 12px 16px;
    border-radius: 10px;

    & > * {
      min-width: 0;
      max-width: 100%;
    }

    @media (min-width: 1024px) {
      display: flex;
      flex-wrap: wrap;
      width: auto;
      padding: 0;
      flex: 1 0 0;
      justify-content: center;
      align-content: center;
      gap: 5px 30px;
    }
  }

  &__responsible {
    @apply flex items-center no-underline;
  }

  &__parent-advisor {
    width: 200px;
    height: auto;

    @media (min-width: 1024px) {
      width: 240px;
      height: auto;
    }
  }

  &__begamble {
    :deep(img) {
      width: 100px;
      height: 18px;
      max-width: none;

      @media (min-width: 1024px) {
        width: auto;
        height: 30px;
      }
    }
  }

  &__gt {
    :deep(img) {
      width: 36px;
      height: 31px;
      max-width: none;

      @media (min-width: 1024px) {
        width: auto;
        height: 30px;
      }
    }
  }

  &__ra {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    text-decoration: none;
    width: auto;
    height: 44px;
    overflow: hidden;
    aspect-ratio: 11 / 6;
    background: transparent;

    @media (min-width: 1024px) {
      width: auto;
      height: 48px;
    }

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__dynamic {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    text-decoration: none;
    width: var(--seal-w-mobile);
    height: var(--seal-h-mobile);

    @media (min-width: 1024px) {
      width: var(--seal-w);
      height: var(--seal-h);
    }

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}
</style>
